import React, {Dispatch, FC, SetStateAction, useEffect, useState} from 'react';
import {Checkbox, Col, Form, Input, message, Radio, Row} from "antd";
import {ScheduleType} from "../../../../../proto/generated/api_entities_pb";
import {Dayjs} from "dayjs";
import {useAppDispatch, useAppSelector} from "../../../../../store/store";
import {clearCreateEvent, createEvent} from "../../../../../store/reducers/scheduleReducer/scheduleReducer";
import {CustomDatePicker} from "../../../../../components/UI/AntCustom/CustomDatePicker";
import {DateAndTimeRangePicker} from "../../../../../components/UI/AntCustom/DateAndTimeRangePicker";
import {DebounceSelect} from "../../../../../components/UI/AntCustom/DebounceSelect";
import {CustomInputNumber} from "../../../../../components/UI/AntCustom/CustomInputNumber";
import WarningIcon from "../../../../../media/icons/warning.svg?react";
import PlaneGreyIcon from "../../../../../media/icons/plane-grey.svg?react";
import {
  fetchAirports,
  fetchInitialAirportForSelect
} from "../../../../../utils/helpers/selectDataFetchers/fetchAirports";
import {fetchOwnPlanes} from "../../../../../utils/helpers/selectDataFetchers/fetchOwnPlanes";
import {useAirportFilter} from "../../../../../utils/hooks/useAirportFilter";
import {renderSuccessModal} from "../../../../../components/UI/StatusModals/renderSuccessModal";
import {CustomModal} from "../../../../../components/UI/AntCustom/CustomModal";
import {CustomFieldInput} from "../../../../../components/UI/AntCustom/CustomFieldInput";
import {CustomButton} from "../../../../../components/UI/AntCustom/CustomButton";
import {defineMessages, useIntl} from "react-intl";
import {CommonFormMessages} from "../../../../../intl/commonMessages/CommonFormMessages";
import {CommonUnitMessages} from "../../../../../intl/commonMessages/CommonUnitMessages";
import {CommonButtonMessages} from "../../../../../intl/commonMessages/CommonButtonMessages";
import {API} from "../../../../../API/API";
import {onFinishTrimmed} from "../../../../../utils/helpers/formHelpers/onFinishTrimmed";
import {AirportApiGetAirportRequest} from "../../../../../proto/generated/api_entities_pb";
import {mapAirportEntityToIAirport} from "../../../../../store/reducers/fleetReducer/fleetMappers";
import {dayjsToUTCDate} from "../../../../../utils/helpers/dateHelpers/dateHelpers";


const ScheduleCreateModalMessages = defineMessages({
  success: {
    id: "scheduleCreateModal.success",
    defaultMessage: "The new event has been successfully added to schedule",
  },
  title: {
    id: "scheduleCreateModal.title",
    defaultMessage: "Add to schedule",
  }
})

interface IScheduleCreateFormData {
  planeId: number;
  eventType: ScheduleType;
  dateRange: [Dayjs, Dayjs];
  airportFromId: number;
  airportToId?: number;
  eventName?: string;
  refuelingCount?: number; // Количество дозаправок (для ORDER_REQUEST и EMPTY_LEG)
  pax?: number; // Количество пассажиров (для ORDER_REQUEST)
  isPublic?: boolean; // Публичный доступ
}

interface IScheduleCreateModal {
  isModalVisible: boolean;
  setIsModalVisible: Dispatch<SetStateAction<boolean>>;
}

export const ScheduleCreateModal: FC<IScheduleCreateModal> = ({isModalVisible, setIsModalVisible}) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [eventTypeRadioValue, setEventTypeRadioValue] = useState<ScheduleType>(ScheduleType.ORDER_REQUEST);
  const [airportFromOffset, setAirportFromOffset] = useState<number | null>(null);
  const [airportToOffset, setAirportToOffset] = useState<number | null>(null);
  const {
    isLoading,
    isSuccess,
    errorMessage,
  } = useAppSelector(state => state.schedule.create);

  useEffect(() => {
    return () => {
      dispatch(clearCreateEvent());
    }
  }, [])

  useEffect(() => {
    if (!isLoading) {
      if (isSuccess)
        renderSuccessModal(intl.formatMessage(ScheduleCreateModalMessages.success), () => setIsModalVisible(false));
      if (errorMessage)
        message.error(errorMessage);
    }
  }, [isSuccess, errorMessage, isLoading])

  const handleFormValuesChange = async (changedValues: any) => {
    const fieldName = Object.keys(changedValues)[0];

    if (fieldName === "eventType") {
      const value = changedValues[fieldName];
      setEventTypeRadioValue(value)
    } else if (["airportFromId", "airportToId"].includes(fieldName)) {
      // Загружаем информацию об аэропортах при изменении
      const airportFromId = fieldName === 'airportFromId' ? changedValues[fieldName] : form.getFieldValue('airportFromId');
      const airportToId = fieldName === 'airportToId' ? changedValues[fieldName] : form.getFieldValue('airportToId');

      if (airportFromId) {
        try {
          const req = new AirportApiGetAirportRequest().setAirportid(airportFromId);
          const airportEntity = (await API.getAirportById(req)).getAirport();
          if (airportEntity) {
            const airport = mapAirportEntityToIAirport(airportEntity);
            setAirportFromOffset(airport.timezone?.offset ?? null);
          } else {
            setAirportFromOffset(null);
          }
        } catch (error) {
          console.error('Failed to load airport from:', error);
          setAirportFromOffset(null);
        }
      } else {
        setAirportFromOffset(null);
      }

      if (airportToId) {
        try {
          const req = new AirportApiGetAirportRequest().setAirportid(airportToId);
          const airportEntity = (await API.getAirportById(req)).getAirport();
          if (airportEntity) {
            const airport = mapAirportEntityToIAirport(airportEntity);
            setAirportToOffset(airport.timezone?.offset ?? null);
          } else {
            setAirportToOffset(null);
          }
        } catch (error) {
          console.error('Failed to load airport to:', error);
          setAirportToOffset(null);
        }
      } else {
        setAirportToOffset(null);
      }
    }
  }

  const onFinish = (values: IScheduleCreateFormData) => {
    dispatch(createEvent({
      eventType: values.eventType,
      eventName: values.eventName,
      planeId: values.planeId,
      dateFrom: dayjsToUTCDate(values.dateRange[0]),
      dateTo: dayjsToUTCDate(values.dateRange[1]),
      airportFromId: values.airportFromId,
      airportToId: values.airportToId,
      refuelingCount: values.refuelingCount,
      pax: values.pax,
      isPublic: values.isPublic,
    }));
  }

  const onFinishFailed = () => {
    message.error(intl.formatMessage(CommonFormMessages.incorrect))
  }

  const getInitialAirportFromCallback = () => {
    const currentAirportFromId = form.getFieldValue('airportFromId');
    if (!currentAirportFromId) {
      return Promise.resolve(undefined);
    }
    return fetchInitialAirportForSelect(currentAirportFromId);
  }

  const getInitialAirportToCallback = () => {
    const currentAirportToId = form.getFieldValue('airportToId');
    if (!currentAirportToId) {
      return Promise.resolve(undefined);
    }
    return fetchInitialAirportForSelect(currentAirportToId);
  }

  const fromAirportFilter = useAirportFilter(form, 'airportToId');
  const toAirportFilter = useAirportFilter(form, 'airportFromId');

  const initialValues: Partial<IScheduleCreateFormData> = {
    planeId: undefined,
    eventType: ScheduleType.ORDER_REQUEST,
    dateRange: undefined,
    airportFromId: undefined,
    airportToId: undefined,
    eventName: undefined,
  }

  return (
    <CustomModal
      title={intl.formatMessage(ScheduleCreateModalMessages.title)}
      open={isModalVisible}
      onCancel={() => setIsModalVisible(false)}
      centered
      footer={null}
      width={700}
    >
      <Form
        form={form}
        onFinish={(values) => onFinishTrimmed(values, onFinish)}
        onFinishFailed={onFinishFailed}
        layout={'vertical'}
        initialValues={initialValues}
        onValuesChange={handleFormValuesChange}
      >
        {(eventTypeRadioValue !== ScheduleType.ORDER_REQUEST && 
          eventTypeRadioValue !== ScheduleType.EMPTY_LEG) &&
          <Col flex={1}>
            <CustomFieldInput
              label={intl.formatMessage(CommonFormMessages.eventNameLabel)}
              name={'eventName'}
              rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
            >
              <Input placeholder={intl.formatMessage(CommonFormMessages.eventNamePlaceholder)}/>
            </CustomFieldInput>
          </Col>
        }

        <Col flex={1}>
          <CustomFieldInput
            label={intl.formatMessage(CommonFormMessages.planeLabel)}
            name={'planeId'}
            rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
          >
            <DebounceSelect
              fetchOptions={fetchOwnPlanes}
              placeholder={intl.formatMessage(CommonFormMessages.planePlaceholder)}
            />
          </CustomFieldInput>
        </Col>

        <Col flex={1}>
          <CustomFieldInput
            label={intl.formatMessage(CommonFormMessages.eventTypeLabel)}
            name={'eventType'}
            rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
          >
            <Radio.Group>
              <Radio
                value={ScheduleType.ORDER_REQUEST}>{intl.formatMessage(CommonFormMessages.eventTypeOrderRequestRadio)}</Radio>;
              <Radio
                value={ScheduleType.EMPTY_LEG}>{intl.formatMessage(CommonFormMessages.eventTypeEmptyLegRadio)}</Radio>;
              <Radio value={ScheduleType.MAINTENANCE}>{intl.formatMessage(CommonFormMessages.eventTypeMaintenanceRadio)}</Radio>;
              <Radio value={ScheduleType.OTHER}>{intl.formatMessage(CommonFormMessages.eventTypeParkingRadio)}</Radio>;
              <Radio value={ScheduleType.CUSTOM}>{intl.formatMessage(CommonFormMessages.eventTypeCustomRadio)}</Radio>;
            </Radio.Group>
          </CustomFieldInput>
        </Col>

        {(eventTypeRadioValue === ScheduleType.ORDER_REQUEST || eventTypeRadioValue === ScheduleType.EMPTY_LEG) &&
          <>
            <Col flex={1}>
              <Row gutter={16}>
                <Col flex="0 1 40%" style={{maxWidth: '40%'}}>
                  <CustomFieldInput
                    label={intl.formatMessage(CommonFormMessages.fromAirportLabel)}
                    name={'airportFromId'}
                    rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
                  >
                    <DebounceSelect
                      key={fromAirportFilter.key}
                      fetchOptions={fromAirportFilter.fetchOptions}
                      placeholder={intl.formatMessage(CommonFormMessages.fromAirportPlaceholder)}
                      showSearch={true}
                      defaultOptionsFetchCallback={getInitialAirportFromCallback}
                    />
                </CustomFieldInput>
              </Col>

              <Col flex={1} style={{display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '8px', paddingLeft: '8px', paddingRight: '8px'}}>
                <PlaneGreyIcon />
                <div style={{display: 'flex', alignItems: 'center', position: 'relative', width: '100%'}}>
                  <div style={{width: '100%', height: '2px', backgroundColor: '#808080', position: 'absolute'}}></div>
                  <div style={{width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#808080', position: 'absolute', right: 0}}></div>
                </div>
              </Col>

              <Col flex="0 1 40%" style={{maxWidth: '40%'}}>
                <CustomFieldInput
                  label={intl.formatMessage(CommonFormMessages.toAirportLabel)}
                  name={'airportToId'}
                  rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
                >
                    <DebounceSelect
                      key={toAirportFilter.key}
                      fetchOptions={toAirportFilter.fetchOptions}
                      placeholder={intl.formatMessage(CommonFormMessages.toAirportPlaceholder)}
                      showSearch={true}
                      defaultOptionsFetchCallback={getInitialAirportToCallback}
                    />
                </CustomFieldInput>
              </Col>
            </Row>
            </Col>
          </>
        }

        {(eventTypeRadioValue === ScheduleType.CUSTOM ||
          eventTypeRadioValue === ScheduleType.MAINTENANCE ||
          eventTypeRadioValue === ScheduleType.OTHER) &&
          <Col flex={1}>
            <CustomFieldInput
              label={intl.formatMessage(CommonFormMessages.stayAirportLabel)}
              name={'airportFromId'}
              rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
            >
              <DebounceSelect
                fetchOptions={fetchAirports}
                placeholder={intl.formatMessage(CommonFormMessages.stayAirportPlaceholder)}
                showSearch={true}
                defaultOptionsFetchCallback={getInitialAirportFromCallback}
              />
            </CustomFieldInput>
          </Col>
        }

        <Col flex={1}>
          <div style={{marginBottom: '16px'}}>
            <CustomFieldInput
              label={`${intl.formatMessage(CommonFormMessages.dateRangeLabel)} (UTC)`}
              name={'dateRange'}
              rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
            >
              <DateAndTimeRangePicker
                format="DD.MM.YYYY"
                placeholder={[intl.formatMessage(CommonUnitMessages.from), intl.formatMessage(CommonUnitMessages.to)]}
                inputReadOnly={true} placement={"bottomLeft"}
                getPopupContainer={trigger => trigger.parentNode as HTMLElement}
                airportFromOffset={airportFromOffset}
                airportToOffset={airportToOffset}
                containerHeight="100%"
              />
            </CustomFieldInput>
            <div style={{color: '#808080', fontSize: '9px', display: 'flex', alignItems: 'center', gap: '4px'}}>
              <WarningIcon style={{width: '12px', height: '12px', fill: '#808080'}} />
              {intl.formatMessage(CommonFormMessages.airportHoursWarning)}
            </div>
          </div>
        </Col>

        {eventTypeRadioValue === ScheduleType.ORDER_REQUEST &&
          <>
            <Col flex={1}>
              <Row gutter={16}>
                <Col flex={1}>
                  <CustomFieldInput
                    label={intl.formatMessage(CommonFormMessages.refuelingCountLabel)}
                    name={'refuelingCount'}
                  >
                    <CustomInputNumber 
                      controls={true} 
                      placeholder={intl.formatMessage(CommonFormMessages.commonPlaceholder)}
                      style={{width: "100%"}}
                      min={0}
                    />
                  </CustomFieldInput>
                </Col>
                <Col flex="0 1 120px">
                  <CustomFieldInput
                    label={intl.formatMessage(CommonFormMessages.paxLabel)}
                    name={'pax'}
                    rules={[
                      {required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)},
                      {min: 1, type: "number", message: intl.formatMessage(CommonFormMessages.ruleMinPax)},
                      {max: 100, type: "number", message: intl.formatMessage(CommonFormMessages.ruleMaxPax)}
                    ]}
                  >
                    <CustomInputNumber 
                      controls={true} 
                      placeholder={intl.formatMessage(CommonFormMessages.paxPlaceholder)}
                      style={{width: "100%"}}
                    />
                  </CustomFieldInput>
                </Col>
              </Row>
            </Col>
          </>
        }

        {eventTypeRadioValue === ScheduleType.EMPTY_LEG &&
          <>
            <Col flex={1}>
              <CustomFieldInput
                label={intl.formatMessage(CommonFormMessages.refuelingCountLabel)}
                name={'refuelingCount'}
              >
                <CustomInputNumber 
                  controls={true} 
                  placeholder={intl.formatMessage(CommonFormMessages.commonPlaceholder)}
                  style={{width: "100%"}}
                  min={0}
                />
              </CustomFieldInput>
            </Col>
          </>
        }

        <Col flex={1}>
          <CustomFieldInput
            name={'isPublic'}
            valuePropName="checked"
          >
            <Checkbox>
              {intl.formatMessage(CommonFormMessages.openForPublicSchedule)}
            </Checkbox>
          </CustomFieldInput>
        </Col>

        <CustomFieldInput>
          <CustomButton
            block
            type={'primary'}
            htmlType={'submit'}
            loading={isLoading}
          >
            {intl.formatMessage(CommonButtonMessages.create)}
          </CustomButton>
        </CustomFieldInput>
      </Form>
    </CustomModal>
  );
};
